import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { SYSTEM_ENV } from "../store/constant";

// project imports
import MainLayout from "../layout/MainLayout";
import Loadable from "../ui-component/Loadable";
import LiveTracking from "../views/direct/LiveTracking";
import EsimActivationManagement from "../views/direct/EsimActivationManagement";
import TokenManagement from "../views/direct/TokenManagement";
import SOSAlert from "../views/direct/SOSAlert";
import HistoryPlayback from "../views/direct/HistoryPlayback";
import SOSUserExp from "../views/direct/SOSUserExp";
import SOSExe from "../views/direct/SOSExe";
import EMCall from "../views/direct/emcall";
import RouteFixing from "../views/direct/RouteFixing";
import RouteETA from "../views/direct/RouteETA";
import TripPlanning from "../views/direct/TripPlanning";
import GetAllCall from "../views/direct/GetAllCall";
import CallDetails from "../views/direct/CallDetails";
import AlertList from "../views/reports/AlertList";
import AlertLog from "../views/reports/AlertLog";
import ActivationLogReport from "../views/reports/ActivationLogReport";
import { decipherEncryption } from "../helper";
import NotAuthorized from "../views/pages/NotAuthorized";
import GpsDataLog from "../views/reports/GpsDataLog";
import EmergencyDataLogs from "views/reports/EmergencyDataLogs";
import ApiDataLog from "../views/reports/ApiDataLog";
import CameraFeedsView from "../pages/CameraFeedsView";
import POIViewer from "../views/direct/POIViewer";
import TripViewer from "views/direct/TripViewer";
import ActivatedDeviceReport from "../views/reports/ActivatedDeviceReport";
import AlertReport from "../views/reports/AlertReport";
import DeviceHealthReport from "../views/reports/DeviceHealthReport";
import UserStatisticsReport from "../views/reports/UserStatisticsReport";
import IncidentReport from "../views/reports/IncidentReport";

import PublicTransportDashboard from "../views/dashboard/super admin dashboard/PublicTransportDashboard";
import PublicSafetyDashboard from "../views/dashboard/super admin dashboard/PublicSafetyDashboard";
import PublicTransportVehicleMonitoringDashboard from "../views/dashboard/super admin dashboard/PublicTransportVehicleMonitoringDashboard";
import ERSSVehiclesDashboard from "../views/dashboard/super admin dashboard/ERSSVehiclesDashboard";
import SOSMonitoringDashboard from "../views/dashboard/super admin dashboard/SOSMonitoringDashboard";
import SOSAnalyticsDashboard from "../views/dashboard/super admin dashboard/SOSAnalyticsDashboard";
import NewMap from "../views/MapComponent/Index";
import HealthPacketLog from "../views/reports/HealthPacketLog";
import POIReport from "../views/reports/POIReport";
import MapPolicy from "../views/pages/MapPolicy";

import Help from "../views/pages/Help";

// School Bus Management System
import SchoolBusDashboard from "../views/schoolbus/SchoolBusDashboard";
import ParentTracking from "../views/schoolbus/ParentTracking";
import SchoolBusTagging from "../views/schoolbus/SchoolBusTagging";
import RouteManagement from "../views/schoolbus/RouteManagement";
import BusAssignment from "../views/schoolbus/BusAssignment";
import ProfileManagement from "../views/schoolbus/ProfileManagement";
import SchoolHolidays from "../views/schoolbus/SchoolHolidays";
import SchoolReports from "../views/schoolbus/SchoolReports";
import AlertsCenter from "../views/schoolbus/AlertsCenter";
import SchoolOnboarding from "../views/schoolbus/SchoolOnboarding";

import M2MRegistrationAdminReview from "../views/pages/M2MRegistrationAdminReview";
import VehicleManufacturerRegistrationAdminReview from "../views/pages/VehicleManufacturerRegistrationAdminReview";
import AIS140DeviceManufacturerRegistrationAdminReview from "../views/pages/AIS140DeviceManufacturerRegistrationAdminReview";
import DeviceModelTechnicalOnboardingAdminList from "../views/pages/DeviceModelTechnicalOnboardingAdminList";

// esim Inventory
import Inventory from "../esimInventory/Inventory/esimInventory";
import BootstrapActivation from "../esimInventory/Inventory/BootstrapActivation";
import CommercialActivation from "../esimInventory/Inventory/CommercialActivation";
import CommercialActivationRequest  from "../esimInventory/Inventory/CreateCommercialActivation";
import KycReport  from "../esimInventory/Inventory/KycReport";
import StockMasterStatusOverview from "../esimInventory/Inventory/StockMasterStatusOverview";
import FactoryStockUpload from "../esimInventory/FactoryManagement/FactoryStockUpload";
import DeviceConfigConsole from "../esimInventory/FactoryManagement/DeviceConfigConsole";
import FactoryDispatch from "../esimInventory/FactoryManagement/FactoryDispatch";
import BulkFotaDispatch from "../esimInventory/FactoryManagement/BulkFotaQueue";
import CertificateScopeMangement from "../views/pages/certificatemanage/CertificateScopeManagement";
import CertificateJobQueue from "../views/pages/certificatemanage/CertificateJobQueue";
import CertificateInstalledResetDevice from "../views/pages/certificatemanage/InstalledCertificateDevices";
import CertificateReviewQueue from "../views/pages/certificatemanage/CertificateReviewQueue";
import CertificateAgentPerformance from "../views/pages/certificatemanage/CertificateAgentPerformance";
import DevicesHealths from "../esimInventory/DeviceHealth";
import OBDData from "../views/forms/OBDData"
import SafeCustody from "../esimInventory/FactoryManagement/SafeCustody"
import DeviceFeatureAssignment from "../esimInventory/FactoryManagement/DeviceFeatureAssignment"

const PrivateRoute = ({ element, roles }) => {
  const myDecipher = decipherEncryption("skytrack");
  // Fall back to localStorage so new windows (opened via window.open) are also authenticated
  const userData =
    sessionStorage.getItem("cookiesData") ||
    localStorage.getItem("cookiesData");
  const data = userData && userData.split("-").map((item) => myDecipher(item));
  const isAuthenticated =
    useSelector((state) => state.login.user.isAuthenticated) ||
    sessionStorage.getItem("isAuthenticated") ||
    localStorage.getItem("isAuthenticated");
  const userRoles = userData && data.length > 2 && data[1]; // Get the user role after login from redux store
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (
    roles &&
    roles.length > 0 &&
    !roles.some((role) => userRoles.includes(role))
  ) {
    // User does not have any of the required roles
    return <NotAuthorized />;
  }
  const normalizedRole = (userRoles || '').toLowerCase().trim();
  const isRestrictedRole = ['teamlead', 'team_lead', 'team lead', 'sos_teamlead', 'desk_ex', 'desk_executive', 'desk executive', 'sos_deskexecutive', 'sos_desk_executive', 'sosexecutive'].includes(normalizedRole);
  console.log(`[ACL] Route Check - Role: ${normalizedRole}, Env: ${SYSTEM_ENV}, Restricted: ${isRestrictedRole}`);

  if (SYSTEM_ENV === 'prod') {
    if (isRestrictedRole) {
      return <NotAuthorized />;
    }
  } else if (SYSTEM_ENV === 'sos') {
    if (!isRestrictedRole) {
      return <NotAuthorized />;
    }
  }
  return element;
};

const applyPrivateRoute = (route) => ({
  ...route,
  element: <PrivateRoute element={route.element} roles={route.roles} />,
});

// dashboard routing
const DashboardDefault = Loadable(
  lazy(() => import("../views/dashboard/Default"))
);

const MorthDashboard = Loadable(
  lazy(() => import("../views/dashboard/Morth"))
);

const DashboardRedirect = () => {
  const myDecipher = decipherEncryption("skytrack");

  const userData =
    sessionStorage.getItem("cookiesData") ||
    localStorage.getItem("cookiesData");

  const data = userData?.split("-").map((item) => myDecipher(item));

  const role = data?.[1]?.toLowerCase();

  switch (role) {
    case "esimmanager":
      return <Navigate to="/inventory" replace />;

    case "factorymanager":
      return <Navigate to="/factory-stock-upload" replace />;

     case "certificateexecutive":
      return <Navigate to="/certificate/job-queue" replace />;
     case "certificateadmin":
      return <Navigate to="/certificate/review-queue" replace />;
     case "certificateteamlead":
      return <Navigate to="/certificate/review-queue" replace />;

    default:
      return <DashboardDefault />;
  }
};
// sample page routing
const SamplePage = Loadable(lazy(() => import("../views/sample-page")));
// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: "/",
  element: <MainLayout />,
  children: [
    {
      path: "dashboard",
      element: <DashboardRedirect />,
    },
    {
      path: "/superadmin-dashboard",
      element: <Navigate to="/superadmin-dashboard/transport" replace />,
      roles: ["superadmin"],
    },
    {
      path: "/superadmin-dashboard/transport",
      element: <PublicTransportDashboard />,
      roles: ["superadmin"],
    },
    {
      path: "/superadmin-dashboard/public-safety",
      element: <PublicSafetyDashboard />,
      roles: ["superadmin"],
    },
    {
    path: "/inventory",
    element: <Inventory />,
    roles: ["esimmanager"],
},
    {
    path: "/commercial",
    element: <CommercialActivation />,
    roles: ["esimmanager"],
},
    {
    path: "/commercial-request",
    element: <CommercialActivationRequest />,
    roles: ["esimmanager"],
},
    {
    path: "/bootstrap-activation",
    element: <BootstrapActivation />,
    roles: ["esimmanager"],
},
    {
    path: "/kycreport",
    element: <KycReport />,
    roles: ["esimmanager"],
},
    {
    path: "/stock-master-reports",
    element: <StockMasterStatusOverview />,
    roles: ["esimmanager","superadmin"],
},
{
  path: "/factory-stock-upload",
  element: <FactoryStockUpload />,
  roles: ["factorymanager"],
},
{
  path: "/safe-custody",
  element: <SafeCustody />,
  roles: ["factorymanager","devicemanufacture","dealer","esimmanager"],
},
{
  path: "/device-config-console",
  element: <DeviceConfigConsole />,
  roles: ["factorymanager"],
},
{
  path: "/factory-dispatch",
  element: <FactoryDispatch />,
  roles: ["factorymanager"],
},
{
  path: "/device-feature-assign",
  element: <DeviceFeatureAssignment />,
  roles: ["factorymanager"],
},
{
  path: "/bulk-dispatch",
  element: <BulkFotaDispatch />,
  roles: ["factorymanager"],
},
    // {
    //   path: "/superadmin-dashboard/m2m-registration-requests",
    //   element: <M2MRegistrationAdminReview />,
    //   roles: ["superadmin"],
    // },
    // {
    //   path: "/superadmin-dashboard/manufacturer-registration-requests",
    //   element: <Navigate to="/superadmin-dashboard/vehicle-manufacturer-registration-requests" replace />,
    //   roles: ["superadmin", "stateadmin"],
    // },
    // {
    //   path: "/superadmin-dashboard/vehicle-manufacturer-registration-requests",
    //   element: <VehicleManufacturerRegistrationAdminReview />,
    //   roles: ["superadmin", "stateadmin"],
    // },
    // {
    //   path: "/superadmin-dashboard/ais-140-device-manufacturer-registration-requests",
    //   element: <AIS140DeviceManufacturerRegistrationAdminReview />,
    //   roles: ["superadmin", "stateadmin"],
    // },
    // {
    //   path: "/superadmin-dashboard/technical-onboarding-requests",
    //   element: <DeviceModelTechnicalOnboardingAdminList />,
    //   roles: ["superadmin"],
    // },
    // {
    //   path: "/morth-dashboard",
    //   element: <MorthDashboard />,
    //   roles: ["superadmin"],
    // },
    {
      path: "/live-tracking",
      element: <LiveTracking />,
      roles: ["superadmin", "stateadmin", "owner", "dto"],
    },
    {
      path: "/esimActivationmanagement",
      element: <EsimActivationManagement />,
      roles: ["superadmin"],
    },
    {
      path: "/tokenmanagement",
      element: <TokenManagement />,
      roles: ["superadmin"],
    },
    {
      path: "/obd-data",
      element: <OBDData />,
      roles: ["superadmin", "devicemanufacture"],
    },
    {
      path: "/sos-alert",
      element: <SOSAlert />,
      roles: ["superadmin", "sosadmin"],
    },
    {
      path: "/sos-lead-exp",
      element: <SOSUserExp />,
      roles: ["superadmin", "sosadmin", "teamlead"],
    },
    {
      path: "/sos-exe",
      element: <SOSExe />,
      roles: ["superadmin", "sosadmin", "teamlead", "desk_ex"],
    },
    {
      path: "/history-playback",
      element: <HistoryPlayback />,
      roles: ["superadmin", "stateadmin", "owner", "dto", "dtorto"],
    },
    {
      path: "/route-fixing",
      element: <RouteFixing />,
      roles: ["superadmin",  "owner", "dto"],
    },
    {
      path: "/route-eta",
      element: <RouteETA />,
      roles: ["owner"],
    },
    {
      path: "/trip-planning",
      element: <TripPlanning />,
      roles: ["owner", "dto"],
    },
    {
      path: "/sos-call-list",
      element: <GetAllCall />,
      roles: ["desk_ex", "teamlead", "sosadmin"],
    },
    {
      path: "/emcall",
      element: <EMCall />,
      roles: ["superadmin", "sosadmin", "desk_ex", "teamlead"],
    },
    {
      path: "/sos-call-details/:call_id",
      element: <CallDetails />,
      roles: ["superadmin", "sosadmin", "desk_ex", "teamlead"],
    },
    {
      path: "/help",
      element: <Help />,
    },
    {
      path: "sample-page",
      element: <SamplePage />,
      roles: ["superadmin"],
    },
    {
      path: "alert-list",
      element: <AlertList />,
      roles: ["superadmin", "sosadmin", "desk_ex", "teamlead"],
    },
    {
      path: '/reports/gps-data-log',
      element: <GpsDataLog />,
      roles: ['superadmin', 'stateadmin']
    },
    {
      path: '/reports/activation-log-report',
      element: <ActivationLogReport />,
      roles: ['superadmin']
    },
    {
      path: '/reports/emergency-data-logs',
      element: <EmergencyDataLogs />,
      roles: ['superadmin']
    },
    {
      path: '/reports/api-data-log',
      element: <ApiDataLog />,
      roles: ['superadmin']
    },
    {
      path: '/reports/alert-log',
      element: <AlertLog />,
      roles: ['superadmin', 'stateadmin', 'dtorto', 'owner']
    },
    {
      path: "/camera-feeds",
      element: <CameraFeedsView />,
      roles: ["superadmin", "stateadmin"],
    },
    // {
    //   path: "/poi-viewer",
    //   element: <POIViewer />,
    //   roles: ["superadmin", "stateadmin", "owner", "dto"],
    // },
    {
      path: '/trip-viewer',
      element: <TripViewer />,
      roles: ["superadmin", "owner", "dto"],
    },
    {
      path: '/access-manage',
      element: <CertificateScopeMangement />,
      roles: ["superadmin","certificateadmin"],
    },
    {
      path: '/devices-healths',
      element: <DevicesHealths />,
      roles: ["superadmin","devicemanufacture"],
    },
    {
      path: '/certificate/job-queue',
      element: <CertificateJobQueue />,
      roles: ["certificateexecutive"],
    },
    {
      path: '/certificate/device-installed',
      element: <CertificateInstalledResetDevice />,
      roles: ["superadmin"],
    },
    {
      path: '/certificate/agent-performance',
      element: <CertificateAgentPerformance />,
      roles: ["certificateadmin"],
    },
    {
      path: '/certificate/review-queue',
      element: <CertificateReviewQueue />,
      roles: ["superadmin","certificateadmin","certificateteamlead"],
    },
    {
      path: '/reports/activated-device-report',
      element: <ActivatedDeviceReport />,
      roles: ['superadmin', 'dealer', 'dtorto', 'devicemanufacture']
    },
    {
      path: '/reports/alert-report',
      element: <AlertReport />,
      roles: ['superadmin', 'stateadmin', 'dtorto', 'owner']
    },
    {
      path: '/reports/device-health-report',
      element: <DeviceHealthReport />,
      roles: ['superadmin', 'stateadmin']
    },
    {
      path: '/reports/user-statistics-report',
      element: <UserStatisticsReport />,
      roles: ['superadmin']
    },
    // {
    //   path: '/reports/incident-report',
    //   element: <IncidentReport />,
    //   roles: ['superadmin']
    // },
    {
      path: '/reports/health-packet-log',
      element: <HealthPacketLog />,
      roles: ['superadmin']
    },
    // {
    //   path: '/reports/poi-report',
    //   element: <POIReport />,
    //   roles: ['superadmin', 'stateadmin', 'owner', 'dto']
    // },
    {
      path: '/map',
      element: <NewMap />,
      roles: ['superadmin']
    },
    {
      path: '/map-policy',
      element: <MapPolicy />
    },
    // School Bus Management System Routes
    // {
    //   path: '/schoolbus',
    //   element: <SchoolBusDashboard />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/parent-tracking',
    //   element: <ParentTracking />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin', 'parent']
    // },
    // {
    //   path: '/schoolbus/bus-tagging',
    //   element: <SchoolBusTagging />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/route-management',
    //   element: <RouteManagement />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/bus-assignment',
    //   element: <BusAssignment />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/profile-management',
    //   element: <ProfileManagement />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/holidays',
    //   element: <SchoolHolidays />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/alerts',
    //   element: <AlertsCenter />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // },
    // {
    //   path: '/schoolbus/reports',
    //   element: <SchoolReports />,
    //   roles: ['superadmin', 'stateadmin', 'schooladmin']
    // }

    // ,
    // {
    //   path: '/schoolbus/onboarding',
    //   element: <SchoolOnboarding />,
    //   roles: ['superadmin', 'stateadmin']
    // }

  ].map((route) => applyPrivateRoute(route)),
};

export default MainRoutes;
